import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { type EntitySelection } from "@salestrak/ui";
import { assigneeRefToEntityItem } from "#app/components/entity-selectors/utils";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import { getInitialCustomFieldValue } from "#app/lib/custom-field-utils";
import { useAppData } from "#app/lib/data";
import { useCreateIssueData } from "#app/hooks/issue-view/use-create-issue-data";
import { useCreateIssueMutation } from "#app/hooks/issue-view/use-create-issue-mutation";
import { useIssueSources } from "#app/hooks/settings/use-issue-source-mutations";
import { useScrollToElement } from "#app/lib/hooks/use-scroll-to-element";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import type { IssueRow } from "#app/lib/data";
import type {
  IssueTypeWithFields,
  IssueTypeFieldDef,
  IssueViewStage,
  CustomFieldDef,
} from "#app/components/issue-view/issue-view-types";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Draft state
// ---------------------------------------------------------------------------

export interface CreateIssueDraft {
  name: string;
  description: string;
  issueTypeId: string | null;
  workflowId: string;
  stageId: string | null;
  issueSourceId: string | null;
  address: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }> | null;
  assignees: EntitySelection[];
  values: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface CreateIssueContextValue {
  draft: CreateIssueDraft;
  updateDraft: (partial: Partial<CreateIssueDraft>) => void;
  updateCustomFieldValue: (cfId: string, value: unknown) => void;

  /** Custom fields for the currently selected issue type */
  issueTypeFields: IssueTypeFieldDef[];
  /** IDs of required custom fields for the selected issue type */
  requiredFieldIds: string[];
  /** Resolved custom field defs (for CustomFieldRenderer) keyed by customFieldId */
  customFieldDefs: CustomFieldDef[];
  /** Custom field defs map for quick lookup */
  customFieldDefsById: Record<string, CustomFieldDef>;

  /** Issue types with fields for the type selector */
  issueTypes: IssueTypeWithFields[];
  /** Stages for the current workflow */
  stages: IssueViewStage[];

  /** Whether data is still loading */
  loading: boolean;
  /** Whether the create mutation is in-flight */
  isSubmitting: boolean;
  /** Whether the form is valid and can be submitted */
  canSubmit: boolean;
  /** Number of required fields (including name) that are still empty */
  missingRequiredCount: number;
  /** Whether a submit has been attempted (triggers error display) */
  submitted: boolean;
  /** Submit the form (calls CreateIssue mutation) */
  submit: () => Promise<void>;
  /** Increments on each failed submit (triggers scroll + bar re-show). */
  submitAttemptCount: number;
  /**
   * True once it's safe to autofocus inputs. Desktop defaults to true;
   * the mobile sheet passes false until its open animation settles
   * (focusing mid-animation opens the virtual keyboard and desyncs
   * the sheet's translateY math — clips the handle, adds phantom padding).
   */
  canAutoFocus: boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CreateIssueContext = createContext<CreateIssueContextValue | null>(null);

/**
 * Returns the create-issue context value, or `null` when not in create mode.
 * Components use this to detect and branch for create mode.
 */
export function useCreateIssueMode() {
  return useContext(CreateIssueContext);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isFieldValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/** Returns the DOM element ID of the first invalid field and the total missing count. */
function validateRequiredFields(
  name: string,
  values: Record<string, unknown>,
  requiredFieldIds: string[],
  requireIssueSource: boolean,
  issueSourceId: string | null,
): { firstInvalidId: string | null; missingCount: number } {
  let firstInvalidId: string | null = null;
  let missingCount = 0;

  if (!name.trim()) {
    firstInvalidId = "create-issue-name";
    missingCount++;
  }
  if (requireIssueSource && isFieldValueEmpty(issueSourceId)) {
    if (!firstInvalidId) firstInvalidId = "issue-source";
    missingCount++;
  }
  for (const fieldId of requiredFieldIds) {
    if (isFieldValueEmpty(values[fieldId])) {
      if (!firstInvalidId) firstInvalidId = `cf-${fieldId}`;
      missingCount++;
    }
  }
  return { firstInvalidId, missingCount };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type CreateIssueProviderProps = Omittable<{
  initialWorkflowId?: string;
  initialStageId?: string;
  // The nested object relaxes too — `Omittable` is not recursive, and the
  // create screens hand this the dialog's own `ModalState.address`.
  initialAddress?: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }>;
  initialName?: string;
  /** Where the create flow was launched from — included in issue create_finish analytics. */
  source?: string;
  onCreated: (issue: IssueRow) => void;
  /** When false, gates inputs from autofocusing (e.g. while a sheet is animating). Defaults to true. */
  canAutoFocus?: boolean;
  children: React.ReactNode;
}>;

export function CreateIssueProvider({
  initialWorkflowId,
  initialStageId,
  initialAddress,
  initialName,
  source,
  onCreated,
  canAutoFocus = true,
  children,
}: CreateIssueProviderProps) {
  const { currentUser, workflows } = useAppData();
  const { settings: issueSourceSettings } = useIssueSourceSettings();
  const requireIssueSource = issueSourceSettings.requireIssueSource;

  // Resolve the effective workflowId:
  // - If provided, use it directly
  // - Otherwise, fall back to the default workflow (or first workflow)
  const resolvedWorkflowId = useMemo(() => {
    if (initialWorkflowId) return initialWorkflowId;
    const defaultWf = workflows.find((wf) => wf.isDefault);
    return defaultWf?.workflowId ?? workflows[0]?.workflowId ?? null;
  }, [initialWorkflowId, workflows]);

  const [draft, setDraft] = useState<CreateIssueDraft>({
    name: initialName ?? "",
    description: "",
    issueTypeId: null,
    workflowId: resolvedWorkflowId ?? "",
    stageId: initialStageId ?? null,
    issueSourceId: null,
    address: initialAddress ?? null,
    assignees: [],
    values: {},
  });

  // Sync draft.workflowId when resolvedWorkflowId settles (e.g. workflows load after mount)
  useEffect(() => {
    if (resolvedWorkflowId && !draft.workflowId) {
      setDraft((prev) => ({ ...prev, workflowId: resolvedWorkflowId }));
    }
  }, [resolvedWorkflowId, draft.workflowId]);

  // Fetch issue types + stages for the draft's current workflow
  const {
    issueTypes,
    stages,
    defaultIssueTypeId,
    defaultStageId,
    loading,
  } = useCreateIssueData(draft.workflowId);

  // Track whether we've applied initial defaults
  const defaultsAppliedRef = useRef(false);

  // Build default assignee selection from the current user. The item shape comes
  // from the shared assignee-ref mapper so label/avatar resolution (suspended
  // muting, name→email fallback) can't fork from the entity system.
  const defaultAssigneeSelection = useMemo<EntitySelection | null>(() => {
    if (!currentUser) return null;
    return {
      id: currentUser.userId,
      poolId: USER_POOL_ID,
      entity: assigneeRefToEntityItem({
        __typename: "EntityRef" as const,
        entityType: "user",
        entityId: currentUser.userId,
        // currentUser is always an active member; the owner-ref user shape
        // requires a non-null memberStatus.
        user: { ...currentUser, memberStatus: "active" },
        team: null,
        isPrimary: true,
      }),
    };
  }, [currentUser]);

  // Apply defaults once data loads
  useEffect(() => {
    if (loading || defaultsAppliedRef.current) return;
    defaultsAppliedRef.current = true;

    setDraft((prev) => {
      const issueTypeId = prev.issueTypeId ?? defaultIssueTypeId;
      const stageId = prev.stageId ?? defaultStageId;

      // Populate default values for the default issue type's fields.
      const issueType = issueTypes.find((it) => it.issueTypeId === issueTypeId);
      const defaultValues: Record<string, unknown> = {};
      if (issueType) {
        for (const f of issueType.fields ?? []) {
          if (f.field) {
            defaultValues[f.customFieldId] = getInitialCustomFieldValue(f.field);
          }
        }
      }

      // Default primary assignee to the current user
      const assignees = prev.assignees.length === 0 && defaultAssigneeSelection
        ? [defaultAssigneeSelection]
        : prev.assignees;

      return { ...prev, issueTypeId, stageId, values: defaultValues, assignees };
    });
  }, [loading, defaultIssueTypeId, defaultStageId, issueTypes, defaultAssigneeSelection]);

  // Pre-populate the source with the workspace's default source, mirroring
  // the fallback create() applies server-side when the input omits one — the
  // form shows what will be saved. Reactive, not a one-shot latch: the
  // catalog resolves independently of the issue-type/stage data
  // (cache-and-network), so a default arriving only with the network
  // response — or absent from a stale cached page — still lands. A source
  // already on the draft is never overwritten.
  const { issueSources } = useIssueSources();
  const defaultIssueSourceId = useMemo(
    () =>
      issueSources.find((s) => s.isDefault && !s.archived)?.issueSourceId ?? null,
    [issueSources],
  );
  useEffect(() => {
    if (!defaultIssueSourceId) return;
    setDraft((prev) =>
      prev.issueSourceId ? prev : { ...prev, issueSourceId: defaultIssueSourceId },
    );
  }, [defaultIssueSourceId]);

  // Computed: fields for the selected issue type
  const selectedIssueType = useMemo(
    () => issueTypes.find((it) => it.issueTypeId === draft.issueTypeId) ?? null,
    [issueTypes, draft.issueTypeId],
  );

  const issueTypeFields = useMemo(
    () => selectedIssueType?.fields ?? [],
    [selectedIssueType],
  );

  // Derived, not fetched: `requiredFieldIds` is not a column on IssueType,
  // it is the required bindings' ids.
  const requiredFieldIds = useMemo(
    () =>
      (selectedIssueType?.fields ?? [])
        .filter((f) => f.required)
        .map((f) => f.customFieldId),
    [selectedIssueType],
  );

  const customFieldDefs = useMemo(
    () =>
      issueTypeFields
        .filter((f): f is IssueTypeFieldDef & { field: CustomFieldDef } => f.field !== null)
        .map((f) => f.field),
    [issueTypeFields],
  );

  const customFieldDefsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const f of customFieldDefs) {
      map[f.customFieldId] = f;
    }
    return map;
  }, [customFieldDefs]);

  // Update draft
  const updateDraft = useCallback((partial: Partial<CreateIssueDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...partial };

      // When issue type changes, reset custom field values to new defaults
      if (partial.issueTypeId !== undefined && partial.issueTypeId !== prev.issueTypeId) {
        const newIssueType = issueTypes.find(
          (it) => it.issueTypeId === partial.issueTypeId,
        );
        const defaultValues: Record<string, unknown> = {};
        if (newIssueType) {
          for (const f of newIssueType.fields ?? []) {
            if (f.field) {
              defaultValues[f.customFieldId] = getInitialCustomFieldValue(f.field);
            }
          }
        }
        next.values = defaultValues;
      }

      return next;
    });
  }, [issueTypes]);

  const updateCustomFieldValue = useCallback((cfId: string, value: unknown) => {
    setDraft((prev) => ({
      ...prev,
      values: { ...prev.values, [cfId]: value },
    }));
  }, []);

  // Submission — enrich the created issue with the stage icon color before notifying parent
  const handleCreated = useCallback(
    (issue: IssueRow) => {
      const stage = stages.find((s) => s.stageId === issue.stageId);
      if (stage?.icon) {
        onCreated({
          ...issue,
          icon: {
            ...issue.icon,
            color: stage.icon.color ?? issue.icon?.color ?? null,
            icon: issue.icon?.icon ?? null,
          },
        });
      } else {
        onCreated(issue);
      }
    },
    [onCreated, stages],
  );
  const { createIssue, isSubmitting } = useCreateIssueMutation({ onSuccess: handleCreated, source });
  const [submitted, setSubmitted] = useState(false);
  const { attemptCount: submitAttemptCount, scrollTo } = useScrollToElement();

  const { canSubmit, missingRequiredCount } = useMemo(() => {
    const result = validateRequiredFields(
      draft.name,
      draft.values,
      requiredFieldIds,
      requireIssueSource,
      draft.issueSourceId,
    );
    return { canSubmit: result.missingCount === 0, missingRequiredCount: result.missingCount };
  }, [draft.name, draft.values, requiredFieldIds, requireIssueSource, draft.issueSourceId]);

  const submit = useCallback(async () => {
    setSubmitted(true);
    const { firstInvalidId } = validateRequiredFields(
      draft.name,
      draft.values,
      requiredFieldIds,
      requireIssueSource,
      draft.issueSourceId,
    );
    if (firstInvalidId) {
      scrollTo(firstInvalidId);
      return;
    }
    await createIssue(draft, customFieldDefsById);
  }, [createIssue, draft, customFieldDefsById, requiredFieldIds, requireIssueSource, scrollTo]);

  // Context value
  const value = useMemo<CreateIssueContextValue>(
    () => ({
      draft,
      updateDraft,
      updateCustomFieldValue,
      issueTypeFields,
      requiredFieldIds,
      customFieldDefs,
      customFieldDefsById,
      issueTypes,
      stages,
      loading,
      isSubmitting,
      canSubmit,
      missingRequiredCount,
      submitted,
      submit,
      submitAttemptCount,
      canAutoFocus,
    }),
    [
      draft,
      updateDraft,
      updateCustomFieldValue,
      issueTypeFields,
      requiredFieldIds,
      customFieldDefs,
      customFieldDefsById,
      issueTypes,
      stages,
      loading,
      isSubmitting,
      canSubmit,
      missingRequiredCount,
      submitted,
      submit,
      submitAttemptCount,
      canAutoFocus,
    ],
  );

  return (
    <CreateIssueContext.Provider value={value}>
      {children}
    </CreateIssueContext.Provider>
  );
}
